import React from 'react'
import {
    Box,
    Button,
    Chip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'

import { FormSaveAction, FormSaveButton } from './FormSaveButton'
import {
    ARTIFACT_LABELS,
    SelectedSetupConfig,
    SetupArtifactKind,
    formatModifiedLabel,
} from './setupArtifacts'
import { resolveSetupOrigin } from './setupChooserTones'
import { SetupOriginLine } from './SetupOriginLine'

export type SelectedBarSummarySection = {
    title: string
    items: string[]
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(1.25, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
        marginBottom: theme.spacing(1),
    },
    main: {
        minWidth: 0,
        flex: '0 1 220px',
    },
    mainWide: {
        flex: 1,
    },
    label: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.palette.text.secondary,
    },
    name: {
        fontWeight: 700,
        fontSize: '1rem',
        lineHeight: 1.3,
        marginTop: 2,
    },
    originRow: {
        marginTop: theme.spacing(0.5),
    },
    meta: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(0.75),
    },
    summary: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: theme.spacing(3),
        minWidth: 0,
        flex: '1 1 280px',
        paddingTop: 2,
    },
    summarySection: {
        minWidth: 0,
        flex: '1 1 140px',
        maxWidth: 280,
    },
    summaryTitle: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    },
    summaryItem: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: theme.palette.text.primary,
        lineHeight: 1.45,
    },
    summaryEmpty: {
        fontSize: '0.875rem',
        fontWeight: 400,
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
        lineHeight: 1.45,
    },
    side: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'flex-start',
        flexShrink: 0,
        alignSelf: 'stretch',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
}))

export const SetupArtifactSelectedBar: React.FC<{
    kind: SetupArtifactKind
    selected: SelectedSetupConfig
    onChange?: () => void
    readinessLabel?: string
    /** Center-column detail (e.g. qualifying method stage lists). */
    summarySections?: SelectedBarSummarySection[]
    saveAction?: FormSaveAction
}> = ({
    kind,
    selected,
    onChange,
    readinessLabel,
    summarySections,
    saveAction,
}) => {
    const classes = useStyles()
    const mandated = !!selected.orgMandated
    const origin = resolveSetupOrigin(selected)
    const ownerLabel = mandated ? 'Organization' : 'Property'
    const modified = formatModifiedLabel(selected.modifiedAt)
    const hasSummary = !!summarySections && summarySections.length > 0

    return (
        <Box className={classes.root}>
            <Box
                className={`${classes.main}${
                    hasSummary ? '' : ` ${classes.mainWide}`
                }`}
            >
                <Typography className={classes.label}>
                    Selected {ARTIFACT_LABELS[kind]}
                </Typography>
                <Typography className={classes.name}>
                    {selected.name}
                </Typography>
                {origin ? (
                    <Box className={classes.originRow}>
                        <SetupOriginLine origin={origin} compact />
                    </Box>
                ) : null}
                {modified ? (
                    <Typography className={classes.meta}>{modified}</Typography>
                ) : null}
                <Box className={classes.chips}>
                    <Chip
                        size="small"
                        label={ownerLabel}
                        variant="outlined"
                        icon={
                            mandated ? (
                                <LockOutlinedIcon style={{ fontSize: 14 }} />
                            ) : undefined
                        }
                        color={mandated ? 'primary' : 'default'}
                    />
                    {readinessLabel ? (
                        <Chip
                            size="small"
                            label={readinessLabel}
                            variant="outlined"
                        />
                    ) : null}
                </Box>
            </Box>
            {hasSummary ? (
                <Box className={classes.summary}>
                    {summarySections!.map((section) => (
                        <Box
                            key={section.title}
                            className={classes.summarySection}
                        >
                            <Typography className={classes.summaryTitle}>
                                {section.title}
                            </Typography>
                            {section.items.length === 0 ? (
                                <Typography className={classes.summaryEmpty}>
                                    None selected
                                </Typography>
                            ) : (
                                section.items.map((item) => (
                                    <Typography
                                        key={item}
                                        className={classes.summaryItem}
                                    >
                                        {item}
                                    </Typography>
                                ))
                            )}
                        </Box>
                    ))}
                </Box>
            ) : null}
            <Box className={classes.side}>
                <Box className={classes.actions}>
                    {onChange && !mandated ? (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SwapHorizIcon />}
                            onClick={onChange}
                        >
                            Change
                        </Button>
                    ) : null}
                    {saveAction ? <FormSaveButton {...saveAction} /> : null}
                    {mandated ? (
                        <Typography variant="caption" color="textSecondary">
                            Read-only — set by organization
                        </Typography>
                    ) : null}
                </Box>
            </Box>
        </Box>
    )
}
